import { apiClient } from '../utils/api-client';
import type {
  BulkRegisterResult,
  RegistrationMeta,
  ScheduleAttendeesResponse,
  ScheduleEvent,
  ScheduleEventPayload,
  ScheduleEventResponse,
  ScheduleOccurrence,
  ScheduleOccurrencesResponse,
} from '../types/schedule-event';

/** Thrown for schedule-specific API errors so the UI can react to the code. */
export class ScheduleEventError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ScheduleEventError';
    this.code = code;
  }
}

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  const error = body.errors?.[0];
  throw new ScheduleEventError(error?.detail || fallback, error?.code);
}

class ScheduleEventsService {
  async listOccurrences(gymSlug: string, from: string, to: string): Promise<ScheduleOccurrence[]> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/schedule_events?from=${from}&to=${to}`
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch schedule');
    }
    const data: ScheduleOccurrencesResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  async create(gymSlug: string, payload: ScheduleEventPayload): Promise<ScheduleEvent> {
    const response = await apiClient.post(`/api/gyms/${gymSlug}/schedule_events`, {
      data: { attributes: payload },
    });
    if (!response.ok) {
      await throwForResponse(response, 'Failed to create event');
    }
    const data: ScheduleEventResponse = await response.json();
    return data.data.attributes;
  }

  async update(gymSlug: string, id: string, payload: ScheduleEventPayload): Promise<ScheduleEvent> {
    const response = await apiClient.patch(`/api/gyms/${gymSlug}/schedule_events/${id}`, {
      data: { attributes: payload },
    });
    if (!response.ok) {
      await throwForResponse(response, 'Failed to update event');
    }
    const data: ScheduleEventResponse = await response.json();
    return data.data.attributes;
  }

  async remove(gymSlug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/schedule_events/${id}`);
    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete event');
    }
  }

  async register(gymSlug: string, eventId: string, occurrenceDate: string): Promise<RegistrationMeta> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/register`,
      { occurrence_date: occurrenceDate }
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to register');
    }
    return response.json();
  }

  async unregister(gymSlug: string, eventId: string, occurrenceDate: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/unregister?occurrence_date=${occurrenceDate}`
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to unregister');
    }
  }

  async attendees(
    gymSlug: string,
    eventId: string,
    occurrenceDate: string
  ): Promise<ScheduleAttendeesResponse> {
    const response = await apiClient.get(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/attendees?occurrence_date=${occurrenceDate}`
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch attendees');
    }
    return response.json();
  }

  /** Admin: enrol a user in every occurrence of a recurring event up to a date. */
  async bulkRegister(
    gymSlug: string,
    eventId: string,
    payload: { user_slug: string; until_date: string }
  ): Promise<BulkRegisterResult> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/bulk_register`,
      payload
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to register user');
    }
    return response.json();
  }

  /** Staff: add a single user to one occurrence (auto-confirmed). */
  async staffRegister(
    gymSlug: string,
    eventId: string,
    payload: { user_slug: string; occurrence_date: string }
  ): Promise<void> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/staff_register`,
      payload
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to register user');
    }
  }

  /** Staff: confirm a pending registration (drawer / in-app notification). */
  async confirmRegistration(registrationId: string): Promise<void> {
    const response = await apiClient.post(`/api/schedule_registrations/${registrationId}/confirm`, {});
    if (!response.ok) {
      await throwForResponse(response, 'Failed to confirm registration');
    }
  }

  /** Staff: remove any user's registration (e.g. to decline a pending one). */
  async removeRegistration(gymSlug: string, eventId: string, registrationId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/remove_registration?registration_id=${registrationId}`
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to remove registration');
    }
  }

  async cancelOccurrence(gymSlug: string, eventId: string, occurrenceDate: string): Promise<ScheduleEvent> {
    const response = await apiClient.post(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/cancel_occurrence`,
      { occurrence_date: occurrenceDate }
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to cancel occurrence');
    }
    const data: ScheduleEventResponse = await response.json();
    return data.data.attributes;
  }

  async restoreOccurrence(gymSlug: string, eventId: string, occurrenceDate: string): Promise<ScheduleEvent> {
    const response = await apiClient.delete(
      `/api/gyms/${gymSlug}/schedule_events/${eventId}/cancel_occurrence?occurrence_date=${occurrenceDate}`
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to restore occurrence');
    }
    const data: ScheduleEventResponse = await response.json();
    return data.data.attributes;
  }
}

export const scheduleEventsService = new ScheduleEventsService();
